import { useEffect, useRef, useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import { SendIcon, RefreshIcon } from '../../assets/icons';
import type { ChatMessage, Revision } from '../../features/aiPlanner/plannerApi';

interface Props {
  messages: ChatMessage[];
  revisions: Revision[];
  suggestions: string[];
  onSend: (text: string) => Promise<unknown>;
  onRevert: (version: number) => Promise<unknown>;
  sending: boolean;
  disabled?: boolean;
}

/** Right-hand pane: the conversation about the plan. Plan-only, brief replies. */
export default function ChatPanel({ messages, revisions, suggestions, onSend, onRevert, sending, disabled }: Props) {
  const [text, setText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const current = revisions[revisions.length - 1]?.version ?? 1;
  const previous = revisions.length >= 2 ? revisions[revisions.length - 2] : null;

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, sending]);

  const send = async (t: string) => {
    const v = t.trim();
    if (!v || sending || disabled) return;
    setText('');
    await onSend(v);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-line-soft px-3 py-2">
        <div>
          <div className="text-sm font-semibold text-ink">Improve the plan</div>
          <div className="text-xs text-ink-secondary">Tell the AI what to change. It edits the plan and replies briefly.</div>
        </div>
        {previous && (
          <Tooltip title={`Go back to version ${previous.version} (${previous.label})`}>
            <Button size="small" type="text" icon={<RefreshIcon className="size-3.5" />}
              disabled={sending || disabled} onClick={() => onRevert(previous.version)}>Undo</Button>
          </Tooltip>
        )}
      </div>

      <div ref={listRef} className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto bg-bg/60 p-3">
        {messages.map((m, i) => (
          <div key={i} className={['flex', m.role === 'user' ? 'justify-end' : 'justify-start'].join(' ')}>
            <div className={['max-w-[92%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm leading-[21px]',
              m.role === 'user' ? 'bg-brand text-white'
                : m.error ? 'border border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C]'
                  : 'border border-line-soft bg-white text-ink'].join(' ')}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-lg border border-line-soft bg-white px-3 py-2 text-sm text-ink-secondary">
              Updating the plan…
            </div>
          </div>
        )}
      </div>

      {suggestions.length > 0 && !disabled && (
        <div className="flex flex-wrap gap-1.5 border-t border-line-soft px-3 py-2">
          {suggestions.map((s) => (
            <button key={s} type="button" disabled={sending} onClick={() => send(s)}
              className="rounded-full border border-line-soft bg-white px-2.5 py-1 text-xs text-ink-secondary transition-colors hover:border-brand hover:text-brand disabled:opacity-50">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 border-t border-line-soft p-2">
        <Input.TextArea
          value={text} onChange={(e) => setText(e.target.value)} disabled={sending || disabled}
          placeholder={disabled ? 'The plan is closed' : 'e.g. Give the food tasks to Sudhir and add a rehearsal two days before'}
          autoSize={{ minRows: 1, maxRows: 4 }}
          onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); void send(text); } }}
        />
        <Tooltip title="Send (Enter)">
          <Button type="primary" icon={<SendIcon className="size-4" />} loading={sending}
            disabled={!text.trim() || disabled} onClick={() => send(text)} />
        </Tooltip>
      </div>
      <div className="px-3 pb-1.5 text-[11px] text-ink-secondary">Version {current} · Shift+Enter for a new line</div>
    </div>
  );
}
